<template>
	<div ref="zoneRef" class="sales-plan-zone">
		<v-alert v-if="!isCardView" type="info" variant="tonal" density="compact">{{ t('FutureReferencingInfo') }}</v-alert>

		<div v-if="!isCardView" class="sales-plan-zone__grid">
			<AgGrid :rowData="displayRows" :getRowId="getRowId" :colDefs="colDefs" :autoGroupColumnDef="autoGroupColumnDef" :groupDefaultExpanded="-1" :getRowClass="getRowClass" rowGroupPanelShow="never" :sideBar="false" :autoHeight="true" />
		</div>

		<TouchCardData v-else :row-data="displayRows" :columns="touchColumns" title-field="name" :subtitle-fields="['ean']" title-image-field="imageThumbnailUid" title-tags-field="productTags" title-product-id-field="productId" row-key="id" metric-layout="rows" :chunk-size="25" :group-columns="['rowGroupCategory']" :group-fields="{ rowGroupCategory: 'category' }" :group-count-label="groupCountLabel" row-class-field="rowClass" sort-storage-key="salesPlan" @cell-change="onCellChange" />
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import AgGrid from '@/components/agGrid/AgGrid.vue';
import TouchCardData from '@/components/agGrid/TouchCardData.vue';
import ChipRenderer from '@/components/agGrid/ChipRenderer.vue';
import ProductLabel from '@/components/common/ProductLabel.vue';
import SwitchRenderer from '@/components/agGrid/SwitchRenderer.vue';
import MultiSelectRenderer from '@/components/agGrid/MultiSelectRenderer.vue';
import { useTranslator } from '@/composables/useTranslator';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';

const t = useTranslator();
const zoneRef = ref(null);
const rows = ref([]);
const reasons = ref([]);
const currency = ref('');
const locale = ref(undefined);
const revision = ref(0);
const isReadonly = ref(false);

let _mountTarget = null;

const isCardView = ref(deviceUtils.isLaptop());
const onResize = () => {
	isCardView.value = deviceUtils.isLaptop();
};

// #region CONTEXT

function handleContext(event) {
	const detail = event?.detail;
	if (!detail) return;
	if (detail.revision && detail.revision === revision.value) return;

	revision.value = detail.revision || 0;
	rows.value = Array.isArray(detail.rows) ? detail.rows : [];
	reasons.value = Array.isArray(detail.reasons) ? detail.reasons : [];
	currency.value = detail.currency || '';
	locale.value = detail.locale || undefined;
	isReadonly.value = !!detail.readonly;
}

onMounted(() => {
	// The event is dispatched on the mount element (= our parentElement). DOM events bubble UP, so we must
	// listen on the parent, not on zoneRef (a child).
	_mountTarget = zoneRef.value?.parentElement;
	_mountTarget?.addEventListener('vue:sales-plan-context', handleContext);
	window.addEventListener('resize', onResize);

	if (_mountTarget?._pendingSalesPlanContext) {
		handleContext({ detail: _mountTarget._pendingSalesPlanContext });
	}
});

onBeforeUnmount(() => {
	_mountTarget?.removeEventListener('vue:sales-plan-context', handleContext);
	window.removeEventListener('resize', onResize);
	_mountTarget = null;
});

// #endregion

// #region WRITE-BACK

// Reports intent to legacy, which owns _salesPlan and every rule (Commandé <-> Non commandable exclusivity,
// "Afficher PV off => qty 0 + Commandé off", readonly/Préparation locking, _orderedEnable), recomputes the
// headers/KPIs and saves the draft. It returns the ALREADY-CORRECTED row synchronously so we repaint in the
// same tick; a structural change returns null and legacy re-dispatches the whole context instead.
function onCellChange({ row, field, value }) {
	if (!row || !field) return;

	const apply = _mountTarget?._salesPlanApply;
	if (typeof apply !== 'function') return;

	const patched = apply({ productId: row.productId, field, value });
	if (!patched) return;

	const index = rows.value.findIndex((candidate) => candidate.productId === patched.productId);
	if (index >= 0) rows.value.splice(index, 1, patched);
}

// #endregion

// #region PRESENTATION

function formatPrice(value) {
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return '';
	return `${numeric.toLocaleString(locale.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency.value}`.trim();
}

function formatDate(value) {
	if (!value) return '';
	const date = new Date(value);
	return isNaN(date.getTime()) ? '' : date.toLocaleDateString(locale.value);
}

function formatNumber(value) {
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return '';
	return numeric.toLocaleString(locale.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const imageThumbnailUidByProductId = computed(() => {
	revision.value;
	return new Map(iframeDataBridge.getProducts().map((product) => [product.id, product.imageThumbnailUid]));
});

function isOOSState(state) {
	return state === 2 || state === 3;
}

const NOT_APPLICABLE_SORT_VALUE = -1;

// Presentation only. `highlight` / `hasFutureMovement` come from legacy and are never re-derived here, so the
// "sortant = no cyan, no clock" carve-out (PO-10) lives in exactly one place.
const displayRows = computed(() =>
	rows.value.map((row) => ({
		...row,
		priceLabel: formatPrice(row.price),
		agreementDateLabel: formatDate(row.agreementDate),
		rowClass: row.highlight ? 'sales-plan-zone__card--future' : null,
		imageThumbnailUid: imageThumbnailUidByProductId.value.get(row.productId) || null,
		productTags: row.hasFutureMovement ? [{ icon: 'schedule', class: 'is-future' }] : [],
		typologyTags: row.typology ? [{ text: row.typology, class: 'chip--current' }] : [],
		futureTags: row.futureStrate ? [{ text: row.futureStrate, class: 'chip--future' }] : [],
		dateTags: row.agreementDate ? [{ icon: 'calendar_month', class: 'is-future' }] : [],
		stateIcons: row.state === 2 ? [{ icon: 'visibility_off', class: 'is-missing' }] : row.state === 3 ? [{ icon: 'block', class: 'is-oos' }] : [],
		selloutAvgVmm12: isOOSState(row.state) ? row.selloutAvgVmm12 : NOT_APPLICABLE_SORT_VALUE,
		selloutValorisation: isOOSState(row.state) ? row.selloutValorisation : NOT_APPLICABLE_SORT_VALUE,
	}))
);

// #endregion

// #region DESKTOP GRID

const getRowId = (params) => String(params.data.id);
const getRowClass = (params) => (params.data?.highlight ? 'sales-plan-zone__row--future' : null);

function dateComparator(first, second) {
	return (first ? new Date(first).getTime() : 0) - (second ? new Date(second).getTime() : 0);
}

// aggFunc: null everywhere - normalizeColDefs would otherwise inject sum/count + enableValue.
const colDefs = computed(() => {
	const cols = [
		{ colId: 'rowGroupCategory', field: 'category', headerName: t('Categories'), rowGroup: true, hide: true, enableRowGroup: false, suppressColumnsToolPanel: true, aggFunc: null },
		{ field: 'name', headerName: t('Label'), minWidth: 260, flex: 2, aggFunc: null, cellRenderer: ProductLabel, cellRendererParams: (params) => ({ subtitle: params.data?.ean, imageThumbnailUid: params.data?.imageThumbnailUid, tags: params.data?.productTags, productId: params.data?.productId }) },
		{ field: 'price', headerName: t('ProductPrice'), minWidth: 110, cellDataType: 'number', aggFunc: null, valueFormatter: ({ value }) => formatPrice(value) },
		{ field: 'typology', headerName: t('Typology'), minWidth: 120, aggFunc: null, cellRenderer: ChipRenderer, cellRendererParams: { textField: null, tagsField: 'typologyTags' } },
		{ field: 'selloutAvgVmm12', headerName: t('dvp_vmm_avg12'), minWidth: 120, cellDataType: 'number', aggFunc: null, valueFormatter: ({ value }) => (value < 0 ? '' : formatNumber(value)) },
		{ field: 'selloutValorisation', headerName: t('dvp_valorisation'), minWidth: 140, cellDataType: 'number', aggFunc: null, valueFormatter: ({ value }) => (value < 0 ? '' : formatPrice(value)) },
		{ field: 'futureStrate', headerName: t('FutureStrate'), minWidth: 130, aggFunc: null, cellRenderer: ChipRenderer, cellRendererParams: { textField: null, tagsField: 'futureTags' } },
		{ field: 'agreementDate', headerName: t('AgreementDate'), minWidth: 150, aggFunc: null, comparator: dateComparator, cellRenderer: ChipRenderer, cellRendererParams: { textField: 'agreementDateLabel', tagsField: 'dateTags' } },
		{ field: 'state', headerName: t('StatementStatus'), minWidth: 100, filter: false, aggFunc: null, cellRenderer: ChipRenderer, cellRendererParams: { textField: null, tagsField: 'stateIcons' } },
		{ field: 'toUse', headerName: t('dvp_ToUse'), minWidth: 120, sortable: false, filter: false, aggFunc: null, cellRenderer: SwitchRenderer, cellRendererParams: { color: 'success', disabledField: 'toUseDisabled', onChange: onCellChange } },
		{
			field: 'quantity',
			headerName: t('dvp_QtyReco'),
			minWidth: 110,
			cellDataType: 'number',
			aggFunc: null,
			editable: (params) => !params.data?.quantityDisabled,
			cellEditor: 'agNumberCellEditor',
			cellEditorParams: { min: 0, precision: 0, step: 1, showStepperButtons: true },
			valueSetter: (params) => {
				onCellChange({ row: params.data, field: 'quantity', value: Number(params.newValue) || 0 });
				return false;
			},
		},
	];

	// Commandé / Non commandable are edit affordances only - hidden entirely in read-only mode rather than shown disabled.
	if (!isReadonly.value) {
		cols.push(
			{ field: 'ordered', headerName: t('dvp_Ordered'), minWidth: 120, sortable: false, filter: false, aggFunc: null, cellRenderer: SwitchRenderer, cellRendererParams: { color: 'primary', disabledField: 'orderedDisabled', onChange: onCellChange } },
			{
				field: 'notOrderableReasons',
				headerName: t('dvp_NotOrderable'),
				minWidth: 320,
				sortable: false,
				filter: false,
				aggFunc: null,
				cellRenderer: MultiSelectRenderer,
				cellRendererParams: () => ({ items: reasons.value, disabledField: 'reasonsDisabled', onChange: onCellChange }),
				valueFormatter: ({ value }) =>
					(Array.isArray(value) ? value : [])
						.map((id) => reasons.value.find((r) => r.id === id)?.name)
						.filter(Boolean)
						.join(', '),
			}
		);
	}

	return cols;
});

// Reproduces the legacy per-category summary line, reusing the very same three keys.
function groupCounterText(leaves) {
	const used = leaves.filter((leaf) => leaf.toUse).length;
	const ordered = leaves.filter((leaf) => leaf.ordered).length;
	return `${leaves.length} ${t('DVPNbProduct')} • ${used} ${t('DVPNbProductUsed')} • ${ordered} ${t('DVPNbProductOrdered')}`;
}

const autoGroupColumnDef = computed(() => ({
	headerName: t('Categories'),
	minWidth: 200,
	cellRendererParams: { suppressCount: true },
	valueFormatter: (params) => (params.node?.group ? `${params.value} — ${groupCounterText((params.node.allLeafChildren || []).map((leaf) => leaf.data).filter(Boolean))}` : params.value),
}));

// #endregion

// #region MOBILE CARDS

const touchColumns = computed(() => {
	const cols = [
		{ field: 'price', label: t('Price'), formatMetric: formatPrice },
		{ field: 'typology', label: t('Typology'), type: 'chip', tagsField: 'typologyTags', sortType: 'text' },
		{ field: 'futureStrate', label: t('FutureStrate'), type: 'chip', tagsField: 'futureTags', sortType: 'text' },
		{ field: 'agreementDateLabel', label: t('AgreementDate'), sortType: 'text', formatMetric: (value) => value || '—' },
		{ field: 'state', label: t('Status'), type: 'icon', tagsField: 'stateIcons' },
		{ field: 'selloutAvgVmm12', label: t('dvp_vmm_avg12'), formatMetric: (value) => (value < 0 ? '—' : formatNumber(value)) },
		{ field: 'selloutValorisation', label: t('dvp_valorisation'), formatMetric: (value) => (value < 0 ? '—' : formatPrice(value)) },
		{ field: 'toUse', label: t('dvp_ToUse'), type: 'switch', color: 'success', disabledField: 'toUseDisabled' },
		{ field: 'quantity', label: t('Quantity'), type: 'input', min: 0, step: 1, disabledField: 'quantityDisabled' },
	];

	if (!isReadonly.value) {
		cols.push({ field: 'ordered', label: t('Ordered'), type: 'switch', color: 'primary', disabledField: 'orderedDisabled' }, { field: 'notOrderableReasons', label: t('dvp_NotOrderable'), type: 'multiselect', items: reasons.value, disabledField: 'reasonsDisabled' });
	}

	return cols;
});

const groupCountLabel = ({ rows: groupRows }) => groupCounterText(groupRows);

// #endregion
</script>

<style lang="scss">
.touch-card-data__metrics .touch-card-data__metric {
	min-height: 40px;
}
</style>
